/**
 * Merenje prostora za home sekcijsku navigaciju u header mid-bar-u (inline vs burger).
 */

import { useEffect } from 'react';

const MOBILE_HEADER_MAX_WIDTH = 1180;
const MID_BAR_GAP_PX = 20;

/**
 * @typedef {Object} UseHomeSectionNavPlacementParams
 * @property {boolean} enabled - Da li je merenje aktivno (npr. samo na `/`).
 * @property {import('react').RefObject<HTMLElement|null>} navRef - Ref na nav kontejner.
 * @property {import('react').RefObject<HTMLElement|null>} listRef - Ref na listu linkova sekcija.
 * @property {string} sectionsKey - Stabilan ključ sekcija za re-merenje pri promeni liste.
 * @property {(placement: 'inline'|'burger') => void} setPlacement - Setter za `placement` u kontekstu.
 */

/**
 * Meri da li sekcijska navigacija staje u mid-bar; ako ne, prebacuje u burger meni.
 * Koristi `ResizeObserver` i `window.resize` za reaktivno ažuriranje.
 * @param {UseHomeSectionNavPlacementParams} params
 */
export function useHomeSectionNavPlacement({
  enabled,
  navRef,
  listRef,
  sectionsKey,
  setPlacement,
}) {
  useEffect(() => {
    if (!enabled || !setPlacement) {
      setPlacement?.('inline');
      return undefined;
    }

    const measure = () => {
      if (window.innerWidth <= MOBILE_HEADER_MAX_WIDTH) {
        setPlacement('burger');
        return;
      }

      const midBar = navRef.current?.closest('.mid-bar');
      const brandRow = navRef.current?.closest('.header-brand-row');
      const list = listRef.current;
      if (!midBar || !brandRow || !list) return;

      const logoBlock = brandRow.querySelector('.header-main-nav');
      const rightMenu = midBar.querySelector(':scope > div:last-child');
      const available =
        midBar.clientWidth -
        (logoBlock?.offsetWidth ?? 0) -
        (rightMenu?.offsetWidth ?? 0) -
        MID_BAR_GAP_PX;

      setPlacement(list.scrollWidth > Math.max(available, 0) ? 'burger' : 'inline');
    };

    measure();

    const ro = new ResizeObserver(measure);
    const midBar = navRef.current?.closest('.mid-bar');
    const rightMenu = midBar?.querySelector(':scope > div:last-child');
    if (midBar) ro.observe(midBar);
    if (rightMenu) ro.observe(rightMenu);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, navRef, listRef, sectionsKey, setPlacement]);
}
